import type { Scenario } from '../types'

/**
 * The Mandarin flat viewing — the scenario where the numbers are the point.
 *
 * Renting is the exchange that turns a misheard figure into money, and Mandarin
 * makes the mishearing easy: 七十 and 十七 are the same two characters in
 * opposite order, and it is the tones doing most of the work of telling them
 * apart. One beat therefore hands the learner a number worth checking rather
 * than nodding at, which is the habit the numbers island exists to build.
 *
 * The landlord speaks a little above the learner's level throughout, as real
 * ones do. A scenario that moderates is not practising the thing that actually
 * goes wrong.
 */

const SEED_CLOCK = 0
const freshStats = () => ({ runs: 0, bestScore: 0, lastScore: null, lastRunAt: null })

interface Beat {
  hanzi: string
  pinyin: string
  english: string
  speaker: string
  cue: string
  island: string
  blocks: string[]
  hint?: string
}

const BEATS: Beat[] = [
  {
    speaker: '房东',
    hanzi: '您好，请进。您想先了解什么？',
    pinyin: 'nín hǎo qǐng jìn nín xiǎng xiān liǎo jiě shén me',
    english: 'Hello, come in. What would you like to know first?',
    cue: 'Ask what it comes to altogether.',
    island: 'isl_shuzi',
    blocks: ['isl_shuzi_b1'],
    hint: '一共 is what gets you the whole figure rather than the bare rent.',
  },
  {
    speaker: '房东',
    hanzi: '房租是九百七十，包括物业费。',
    pinyin: 'fáng zū shì jiǔ bǎi qī shí bāo kuò wù yè fèi',
    english: 'The rent is nine hundred and seventy, service charge included.',
    cue: 'You are not certain whether that was seventy or seventeen.',
    island: 'isl_shuzi',
    blocks: ['isl_shuzi_b3'],
    hint: '七十 and 十七 are the same two characters reversed. Checking is normal; guessing is what costs you.',
  },
  {
    speaker: '房东',
    hanzi: '七十。九百七十。',
    pinyin: 'qī shí jiǔ bǎi qī shí',
    english: 'Seventy. Nine hundred and seventy.',
    cue: 'Get that number in writing before you forget it.',
    island: 'isl_shuzi',
    blocks: ['isl_shuzi_b4'],
  },
  {
    speaker: '房东',
    hanzi: '当然可以。您什么时候要住进来？',
    pinyin: 'dāng rán kě yǐ nín shén me shí hou yào zhù jìn lai',
    english: 'Of course. When would you want to move in?',
    cue: 'You arrive on the fourteenth of March.',
    island: 'isl_riqi',
    blocks: ['isl_riqi_b2'],
    hint: 'Month before day, and the whole time phrase before the verb.',
  },
  {
    speaker: '房东',
    hanzi: '可以。您什么时候方便再来看一次？',
    pinyin: 'kě yǐ nín shén me shí hou fāng biàn zài lái kàn yí cì',
    english: 'That works. When would suit you to come and see it again?',
    cue: 'Weekdays suit you.',
    island: 'isl_riqi',
    blocks: ['isl_riqi_b4'],
  },
]

export function buildNumberScenariosZh(): Scenario[] {
  return [
    {
      id: 'scn_zh_kanfangzi',
      language: 'zh',
      title: 'The numbers that cost money',
      titleZh: '看房子',
      setting: '一次看房，周六上午，楼下还有三个人在等',
      brief:
        'A flat viewing on a Saturday morning with three other people waiting downstairs. The landlord is friendly and quick, and every figure he says matters — one of them is worth checking rather than nodding at.',
      isStarter: true,
      updatedAt: SEED_CLOCK,
      stats: freshStats(),
      beats: BEATS.map((beat, i) => ({
        id: `scn_zh_kanfangzi_b${i + 1}`,
        speaker: beat.speaker,
        promptHanzi: beat.hanzi,
        promptPinyin: beat.pinyin,
        promptEnglish: beat.english,
        cue: beat.cue,
        expectIslandId: beat.island,
        expectBlockIds: beat.blocks,
        ...(beat.hint ? { hint: beat.hint } : {}),
      })),
    },
  ]
}
